import { useState } from "react";
import { useAccount, useConnect, useDisconnect } from "wagmi";
import { useWallet } from "@solana/wallet-adapter-react";
import { useTonAddress, useTonConnectUI, useTonWallet } from "@tonconnect/ui-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from "react-i18next";
import { Wallet, LogOut, ExternalLink, Copy, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CHAIN_ID_TO_NETWORK } from "@/lib/web3-config";

const ConnectWalletButton = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  
  // EVM
  const { address: evmAddress, isConnected: evmConnected, chain } = useAccount();
  const { connectors, connect, isPending: evmPending } = useConnect();
  const { disconnect: evmDisconnect } = useDisconnect();
  
  // Solana
  const { 
    publicKey: solPublicKey, connected: solConnected, 
    wallets: solWallets, select: solSelect, 
    connect: solConnect, disconnect: solDisconnect,
    connecting: solConnecting 
  } = useWallet();
  
  // TON
  const tonAddress = useTonAddress();
  const tonWallet = useTonWallet();
  const [tonConnectUI] = useTonConnectUI();
  const tonConnected = !!tonWallet;
  
  // Bitcoin
  const [btcAddress, setBtcAddress] = useState<string | null>(null);
  const btcConnected = !!btcAddress;
  const [btcConnecting, setBtcConnecting] = useState(false);

  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const isAnyConnected = evmConnected || solConnected || tonConnected || btcConnected;
  const displayAddress = evmAddress || solPublicKey?.toBase58() || tonAddress || btcAddress;
  
  const networkLabel = evmConnected && chain 
    ? (CHAIN_ID_TO_NETWORK[chain.id] || chain.name)
    : solConnected ? 'Solana' 
    : tonConnected ? 'TON' 
    : btcConnected ? 'Bitcoin'
    : '';

  const copyAddress = () => {
    if (displayAddress) {
      navigator.clipboard.writeText(displayAddress);
      setCopied(true);
      toast({ title: t('wallet.copied') });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shortenAddress = (addr: string) => 
    `${addr.slice(0, 6)}...${addr.slice(-4)}`;

  const handleDisconnect = () => {
    if (evmConnected) evmDisconnect();
    if (solConnected) solDisconnect();
    if (tonConnected) tonConnectUI.disconnect();
    if (btcConnected) setBtcAddress(null);
  };

  const connectBitcoin = async (walletType: 'xverse' | 'unisat') => {
    setBtcConnecting(true);
    try {
      if (walletType === 'unisat') {
        // Unisat wallet
        const unisat = (window as any).unisat;
        if (!unisat) {
          window.open('https://unisat.io/download', '_blank');
          toast({ title: 'Unisat غير مثبتة', description: 'يرجى تثبيت محفظة Unisat أولاً', variant: "destructive" });
          return;
        }
        const accounts = await unisat.requestAccounts();
        if (accounts?.[0]) {
          setBtcAddress(accounts[0]);
          setOpen(false);
        }
      } else {
        // Xverse wallet via sats-connect
        try {
          const { getAddress, AddressPurpose } = await import('sats-connect');
          await getAddress({
            payload: {
              purposes: [AddressPurpose.Payment],
              message: 'Connect to CryptoPay',
              network: { type: 'Mainnet' as any },
            },
            onFinish: (response: any) => {
              const paymentAddr = response.addresses?.find((a: any) => a.purpose === 'payment');
              if (paymentAddr?.address) {
                setBtcAddress(paymentAddr.address);
                setOpen(false);
              }
            },
            onCancel: () => {},
          });
        } catch {
          window.open('https://www.xverse.app/download', '_blank');
          toast({ title: 'Xverse غير مثبتة', description: 'يرجى تثبيت محفظة Xverse أولاً', variant: "destructive" });
        }
      }
    } catch (error: any) {
      toast({ title: t('common.error'), description: error.message?.slice(0, 80) || 'فشل الاتصال', variant: "destructive" });
    } finally {
      setBtcConnecting(false);
    }
  };

  if (isAnyConnected && displayAddress) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-secondary border border-border">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs font-mono text-foreground">{shortenAddress(displayAddress)}</span>
          <button onClick={copyAddress} className="text-muted-foreground hover:text-primary transition-colors">
            {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
        {networkLabel && (
          <span className="text-[10px] text-muted-foreground px-2 py-1 rounded-lg bg-secondary">
            {networkLabel}
          </span>
        )}
        <Button variant="ghost" size="sm" onClick={handleDisconnect} className="h-8 px-2">
          <LogOut className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-2 border-primary/30 text-primary hover:bg-primary/10">
        <Wallet className="w-4 h-4" />
        <span className="text-xs font-medium">{t('wallet.connectWallet') || 'ربط المحفظة'}</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">{t('wallet.connectWallet') || 'ربط المحفظة'}</DialogTitle>
            <DialogDescription className="text-center text-xs">
              {t('wallet.connectWalletDesc') || 'اختر الشبكة والمحفظة'}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="evm" className="mt-2">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="evm" className="text-xs">EVM</TabsTrigger>
              <TabsTrigger value="solana" className="text-xs">Solana</TabsTrigger>
              <TabsTrigger value="ton" className="text-xs">TON</TabsTrigger>
              <TabsTrigger value="btc" className="text-xs">Bitcoin</TabsTrigger>
            </TabsList>
            
            {/* EVM Tab */}
            <TabsContent value="evm" className="space-y-2 mt-3">
              <p className="text-[10px] text-muted-foreground text-center mb-2">Ethereum, BSC, Polygon, Arbitrum, Optimism, Base, Avalanche</p>
              {connectors.map((connector) => (
                <button
                  key={connector.uid}
                  onClick={() => { connect({ connector }); setOpen(false); }}
                  disabled={evmPending}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-border bg-secondary/50 hover:bg-secondary hover:border-primary/30 transition-all disabled:opacity-50"
                >
                  <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                    {connector.name.toLowerCase().includes('metamask') ? <span className="text-lg">🦊</span>
                    : connector.name.toLowerCase().includes('walletconnect') ? <span className="text-lg">🔗</span>
                    : <Wallet className="w-4 h-4 text-primary" />}
                  </div>
                  <div className="text-right flex-1">
                    <p className="text-sm font-semibold text-foreground">{connector.name}</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              ))}
            </TabsContent>
            
            {/* Solana Tab */}
            <TabsContent value="solana" className="space-y-2 mt-3">
              <p className="text-[10px] text-muted-foreground text-center mb-2">Solana Network</p>
              {solWallets.map((wallet) => (
                <button
                  key={wallet.adapter.name}
                  onClick={async () => {
                    solSelect(wallet.adapter.name);
                    try { await solConnect(); } catch {}
                    setOpen(false);
                  }}
                  disabled={solConnecting}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-border bg-secondary/50 hover:bg-secondary hover:border-primary/30 transition-all disabled:opacity-50"
                >
                  <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center overflow-hidden">
                    {wallet.adapter.icon ? <img src={wallet.adapter.icon} alt={wallet.adapter.name} className="w-5 h-5" /> : <span className="text-lg">👻</span>}
                  </div>
                  <div className="text-right flex-1">
                    <p className="text-sm font-semibold text-foreground">{wallet.adapter.name}</p>
                    <p className="text-[10px] text-muted-foreground">{wallet.readyState === 'Installed' ? 'مُثبّتة' : 'اضغط للتحميل'}</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              ))}
              {solWallets.length === 0 && (
                <div className="py-4 text-center">
                  <p className="text-sm text-muted-foreground">لا توجد محافظ Solana</p>
                  <a href="https://phantom.app/download" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-1 inline-block">تحميل Phantom</a>
                </div>
              )}
            </TabsContent>
            
            {/* TON Tab */}
            <TabsContent value="ton" className="space-y-2 mt-3">
              <p className="text-[10px] text-muted-foreground text-center mb-2">TON Network (Tonkeeper, MyTonWallet...)</p>
              <button
                onClick={() => { tonConnectUI.openModal(); setOpen(false); }}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-border bg-secondary/50 hover:bg-secondary hover:border-primary/30 transition-all"
              >
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <span className="text-lg">💎</span>
                </div>
                <div className="text-right flex-1">
                  <p className="text-sm font-semibold text-foreground">TON Wallet</p>
                  <p className="text-[10px] text-muted-foreground">Tonkeeper, MyTonWallet, OpenMask</p>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </TabsContent>

            {/* Bitcoin Tab */}
            <TabsContent value="btc" className="space-y-2 mt-3">
              <p className="text-[10px] text-muted-foreground text-center mb-2">Bitcoin Network</p>
              <button
                onClick={() => connectBitcoin('xverse')}
                disabled={btcConnecting}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-border bg-secondary/50 hover:bg-secondary hover:border-primary/30 transition-all disabled:opacity-50"
              >
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <span className="text-lg">🟧</span>
                </div>
                <div className="text-right flex-1">
                  <p className="text-sm font-semibold text-foreground">Xverse</p>
                  <p className="text-[10px] text-muted-foreground">محفظة Bitcoin متعددة الاستخدامات</p>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
              <button
                onClick={() => connectBitcoin('unisat')}
                disabled={btcConnecting}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-border bg-secondary/50 hover:bg-secondary hover:border-primary/30 transition-all disabled:opacity-50"
              >
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <span className="text-lg">🔶</span>
                </div>
                <div className="text-right flex-1">
                  <p className="text-sm font-semibold text-foreground">Unisat</p>
                  <p className="text-[10px] text-muted-foreground">محفظة Bitcoin و Ordinals</p>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConnectWalletButton;
